import React, { useEffect } from 'react';

const CAPTheorem = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'CAP Theorem: Consistency vs Availability - System Design Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the CAP Theorem in distributed systems. Understand the trade-offs between Consistency, Availability, and Partition tolerance with real-world examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the CAP Theorem in distributed systems. Understand the trade-offs between Consistency, Availability, and Partition tolerance with real-world examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>CAP Theorem: The Fundamental Trade-off</h1>
        <p style={styles.subtitle}>
          Understanding the impossible choice between Consistency, Availability, and Partition tolerance
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is the CAP Theorem?</h2>
          <p style={styles.paragraph}>
            The CAP Theorem, proposed by Eric Brewer in 2000, states that in any distributed system, you can only 
            guarantee two out of the following three properties simultaneously:
          </p>
          <p style={styles.paragraph}>
            <strong>Consistency (C):</strong> All nodes see the same data simultaneously. Every read receives the most 
            recent write or an error.
          </p>
          <p style={styles.paragraph}>
            <strong>Availability (A):</strong> The system remains operational and every request receives a response 
            (success or failure).
          </p>
          <p style={styles.paragraph}>
            <strong>Partition Tolerance (P):</strong> The system continues to operate despite network failures or 
            message loss between nodes.
          </p>
          <p style={styles.paragraph}>
            The fundamental insight is that in the presence of a network partition, you must choose between consistency 
            and availability. You cannot have both.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>The Three Possible Combinations</h2>
          
          <h3 style={styles.heading}>CP Systems: Consistency + Partition Tolerance</h3>
          <p style={styles.paragraph}>
            CP systems sacrifice availability to maintain consistency during network partitions. When nodes cannot 
            communicate with the majority, they become unavailable rather than serving potentially stale data.
          </p>
          <pre style={styles.codeBlock}>
{`Examples: MongoDB, Redis Cluster, HBase, BigTable

Behavior during partition:
- Nodes that cannot communicate with the majority become unavailable
- Only the majority partition continues to serve requests
- Ensures all reads return the latest write
- May result in service downtime for minority partitions

Use Cases:
✓ Financial systems requiring strict consistency
✓ Inventory management systems
✓ Systems where stale data is unacceptable`}
          </pre>

          <h3 style={styles.heading}>AP Systems: Availability + Partition Tolerance</h3>
          <p style={styles.paragraph}>
            AP systems sacrifice consistency to maintain availability during network partitions. All nodes remain 
            available and continue serving requests, but different partitions may have different data temporarily.
          </p>
          <pre style={styles.codeBlock}>
{`Examples: Cassandra, DynamoDB, CouchDB, DNS

Behavior during partition:
- All nodes remain available and continue serving requests
- Different partitions may have different data (temporary inconsistency)
- Eventually consistent when partition heals
- No service downtime

Use Cases:
✓ Social media feeds
✓ Content delivery networks
✓ Shopping carts
✓ Systems where availability is critical`}
          </pre>

          <h3 style={styles.heading}>CA Systems: Consistency + Availability</h3>
          <p style={styles.paragraph}>
            CA systems work only when there are no network partitions. These are typically single-node systems or 
            tightly coupled clusters that cannot handle network partitions gracefully.
          </p>
          <pre style={styles.codeBlock}>
{`Examples: Traditional RDBMS (PostgreSQL, MySQL), LDAP

Behavior:
- Perfect consistency and availability when network is healthy
- Cannot handle network partitions gracefully
- Usually single-node or tightly coupled cluster systems

Use Cases:
✓ Traditional applications with single database
✓ Systems with reliable network infrastructure
✓ When network partitions are extremely rare`}
          </pre>
          <p style={styles.paragraph}>
            In practice, network partitions are inevitable in distributed systems, so the choice is really between 
            CP and AP systems.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World CAP Theorem Examples</h2>
          
          <h3 style={styles.heading}>Amazon DynamoDB (AP System)</h3>
          <p style={styles.paragraph}>
            Amazon's shopping cart system prioritizes availability over consistency. During a network partition, both 
            data centers continue serving requests, which may lead to temporary inconsistency but ensures the shopping 
            experience continues.
          </p>
          <pre style={styles.codeBlock}>
{`Scenario: Network partition between data centers

AP Behavior:
1. Both data centers continue serving requests
2. User adds item to cart in US-East: ["laptop", "mouse"]
3. User adds item to cart in US-West: ["laptop", "keyboard"]  
4. Temporary inconsistency: two different cart states
5. When partition heals, conflict resolution merges: ["laptop", "mouse", "keyboard"]

Result: 
✓ Cart always available (user can shop)
✓ Eventually consistent (all items preserved)
✗ Temporary inconsistency (different views)`}
          </pre>

          <h3 style={styles.heading}>Google Spanner (CP System)</h3>
          <p style={styles.paragraph}>
            Google's globally distributed database prioritizes consistency for financial transactions. During a partition, 
            minority nodes become unavailable rather than serving potentially inconsistent data.
          </p>
          <pre style={styles.codeBlock}>
{`Scenario: Bank transfer between accounts

CP Behavior:
1. Network partition isolates minority of nodes
2. Transfer request: $100 from Account A to Account B
3. Minority partition cannot reach consensus
4. Transaction rejected with error: "Service temporarily unavailable"
5. Only majority partition processes transactions

Result:
✓ Strong consistency (no double spending)
✓ Data integrity maintained
✗ Service unavailable for minority partition`}
          </pre>

          <h3 style={styles.heading}>DNS System (AP System)</h3>
          <p style={styles.paragraph}>
            The Domain Name System prioritizes availability for internet functionality. DNS servers continue resolving 
            domain names even during partitions, occasionally serving stale records but keeping the internet functional.
          </p>
          <pre style={styles.codeBlock}>
{`Scenario: DNS server network partition

AP Behavior:
1. DNS servers continue resolving domain names
2. Different servers may have slightly different records
3. TTL (Time To Live) allows for eventual consistency
4. Users can always access websites (availability)
5. Occasional stale DNS records (consistency trade-off)

Result:
✓ Internet remains functional
✓ High availability for name resolution
✗ Temporary inconsistency in DNS records`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Implementation Examples</h2>
          
          <h3 style={styles.heading}>CP System Implementation</h3>
          <p style={styles.paragraph}>
            Here's how a CP system might implement consensus-based operations that prioritize consistency over availability:
          </p>
          <pre style={styles.codeBlock}>
{`class CPDatabase:
    def __init__(self, nodes):
        self.nodes = nodes
        self.majority = len(nodes) // 2 + 1
    
    def write(self, key, value):
        """Write with strong consistency (CP)"""
        # Phase 1: Prepare
        prepare_responses = []
        for node in self.nodes:
            try:
                response = node.prepare_write(key, value)
                prepare_responses.append(response)
            except NetworkException:
                # Node unreachable due to partition
                continue
        
        # Check if we have majority
        if len(prepare_responses) < self.majority:
            raise UnavailableException("Cannot reach majority of nodes")
        
        # Phase 2: Commit
        commit_responses = []
        for node in prepare_responses:
            try:
                node.commit_write(key, value)
                commit_responses.append(node)
            except NetworkException:
                # Handle commit failures
                self.rollback(prepare_responses, key)
                raise UnavailableException("Commit failed")
        
        return "Write successful"
    
    def read(self, key):
        """Read with strong consistency"""
        responses = []
        for node in self.nodes:
            try:
                value = node.read(key)
                responses.append(value)
            except NetworkException:
                continue
        
        if len(responses) < self.majority:
            raise UnavailableException("Cannot reach majority")
        
        # Return the most recent value (with highest timestamp)
        return max(responses, key=lambda x: x.timestamp).value`}
          </pre>

          <h3 style={styles.heading}>AP System Implementation</h3>
          <p style={styles.paragraph}>
            Here's how an AP system might implement eventually consistent operations that prioritize availability:
          </p>
          <pre style={styles.codeBlock}>
{`class APDatabase:
    def __init__(self, nodes):
        self.nodes = nodes
        self.local_node = nodes[0]  # Current node
    
    def write(self, key, value):
        """Write with high availability (AP)"""
        # Write to local node immediately
        timestamp = time.time()
        self.local_node.write(key, value, timestamp)
        
        # Asynchronously replicate to other nodes
        self.async_replicate(key, value, timestamp)
        
        return "Write successful"  # Always succeeds
    
    def async_replicate(self, key, value, timestamp):
        """Background replication with conflict resolution"""
        for node in self.nodes[1:]:  # Skip local node
            try:
                # Use vector clocks or timestamps for conflict resolution
                node.replicate_write(key, value, timestamp)
            except NetworkException:
                # Queue for later retry when partition heals
                self.queue_for_retry(node, key, value, timestamp)
    
    def read(self, key):
        """Read from local node (may be stale)"""
        try:
            return self.local_node.read(key)
        except Exception:
            # Try other nodes if local fails
            for node in self.nodes[1:]:
                try:
                    return node.read(key)
                except NetworkException:
                    continue
            raise UnavailableException("All nodes unreachable")
    
    def resolve_conflicts(self, key, values):
        """Conflict resolution strategy"""
        # Last-writer-wins based on timestamp
        return max(values, key=lambda x: x.timestamp)
        
        # Alternative: Merge values (for shopping cart)
        # return merge_shopping_cart_items(values)`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Beyond CAP: The PACELC Theorem</h2>
          <p style={styles.paragraph}>
            The PACELC theorem extends CAP by considering trade-offs during normal operation (no partitions). It states: 
            "If there is a Partition (P), choose between Availability (A) and Consistency (C), Else (E) choose between 
            Latency (L) and Consistency (C)."
          </p>
          <p style={styles.paragraph}>
            This recognizes that even when there are no partitions, distributed systems must still choose between 
            low latency and strong consistency. For example, DynamoDB chooses availability during partitions and 
            low latency during normal operation (PA/EL), while MongoDB chooses consistency in both cases (PC/EC).
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Choosing Your CAP Trade-off</h2>
          
          <h3 style={styles.heading}>Choose CP When:</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              Data correctness is critical (financial transactions, inventory management)
            </li>
            <li style={styles.listItem}>
              Stale or incorrect data could cause significant business problems
            </li>
            <li style={styles.listItem}>
              You can tolerate temporary unavailability during network issues
            </li>
            <li style={styles.listItem}>
              ACID properties are required for your use case
            </li>
          </ul>

          <h3 style={styles.heading}>Choose AP When:</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              System must always be available for user-facing applications
            </li>
            <li style={styles.listItem}>
              Temporary inconsistency is acceptable for your business logic
            </li>
            <li style={styles.listItem}>
              You can implement effective conflict resolution mechanisms
            </li>
            <li style={styles.listItem}>
              Eventual consistency meets your business requirements
            </li>
          </ul>
          <p style={styles.paragraph}>
            Many systems use different consistency levels for different operations. For example, user profiles might 
            be eventually consistent while financial transactions require strong consistency.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Understand your requirements:</strong> Clearly define what consistency and availability mean 
              for your specific use case and business requirements.
            </li>
            <li style={styles.listItem}>
              <strong>Design for partition scenarios:</strong> Plan how your system will behave during network 
              failures and test these scenarios regularly.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor partition events:</strong> Track network partitions and their impact on your system 
              to understand real-world behavior.
            </li>
            <li style={styles.listItem}>
              <strong>Implement graceful degradation:</strong> Provide reduced functionality rather than complete 
              failure when facing CAP trade-offs.
            </li>
            <li style={styles.listItem}>
              <strong>Test partition scenarios:</strong> Use chaos engineering to test your system's behavior 
              during network partitions and failures.
            </li>
            <li style={styles.listItem}>
              <strong>Consider hybrid approaches:</strong> Use different consistency models for different types 
              of data within the same system.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            The CAP theorem is not about choosing a database technology, but about understanding fundamental trade-offs 
            in distributed systems. Every distributed system must make this choice, and the decision should be based 
            on specific business requirements and use cases.
          </p>
          <p style={styles.paragraph}>
            Remember that there is no "best" choice - only the right choice for your specific requirements. The key 
            is to make informed decisions and design your system to handle the chosen trade-offs gracefully. Understanding 
            CAP helps you reason about system behavior during failures and make better architectural decisions.
          </p>
        </section>
      </main>
    </article>
  );
};

export default CAPTheorem;